import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, X, FlaskConical, Undo2, ArrowUpRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";

/*  Review Queue
 *  Profiles that look like leftover test / demo data are quarantined here
 *  instead of being deleted. They disappear from the customer directory and
 *  from Find Duplicates, and any row can be sent back to the active database
 *  by a team member after a manual check.
 */
export default function ReviewQueueTab({ active, onChanged }) {
  const { staff } = useAuth();
  const canManage = hasPermission(staff, "brand.customers.delete");

  const [scan, setScan] = useState(null);       // {matched, profiles, total_active}
  const [queue, setQueue] = useState({ total: 0, rows: [] });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        staffApi.get("/customers/review-queue/scan"),
        staffApi.get("/customers/review-queue/list", { params: q ? { search: q } : {} }),
      ]);
      setScan(s.data);
      setQueue(l.data || { total: 0, rows: [] });
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (active) load(""); }, [active, load]);

  const quarantineAll = async () => {
    if (!canManage || !scan?.matched) return;
    if (!window.confirm(
      `Move ${scan.matched} suspected test profile${scan.matched === 1 ? "" : "s"} to the Review Queue?\n\n` +
      "Nothing is deleted — they leave the active customer database and the duplicate finder until a team member restores them."
    )) return;
    setBusy(true);
    try {
      const r = await staffApi.post("/customers/review-queue/quarantine", { all_matched: true });
      toast.success(`${r.data.quarantined} profiles moved to review`);
      await load(search);
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const restore = async (ids) => {
    if (!canManage || !ids.length) return;
    setBusy(true);
    try {
      const r = await staffApi.post("/customers/review-queue/restore", { ids });
      toast.success(`${r.data.restored} profile${r.data.restored === 1 ? "" : "s"} back in the active database`);
      await load(search);
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const allOnPage = useMemo(() => queue.rows.map((r) => r.id), [queue.rows]);
  const allChecked = allOnPage.length > 0 && allOnPage.every((id) => selected.has(id));

  return (
    <div data-testid="review-queue-tab">
      {/* Scanner strip */}
      <Card className="rounded-lg mb-3 border-amber-200 bg-amber-50/60">
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <FlaskConical className="w-4 h-4 text-amber-700 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-900" data-testid="review-scan-summary">
              {loading && !scan
                ? "Scanning the directory…"
                : scan?.matched
                  ? `${scan.matched} active profile${scan.matched === 1 ? "" : "s"} look like test data`
                  : "No test-looking profiles left in the active database"}
            </p>
            <p className="text-[11px] text-amber-800/80">
              Matches on test email domains (example.com, test.com…) and seed names (Old Name, Zebra, Fresh Person…).
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8" onClick={() => load(search)} disabled={loading || busy} data-testid="review-refresh-btn">
              {loading ? "Loading…" : "Rescan"}
            </Button>
            {canManage && !!scan?.matched && (
              <Button size="sm" className="h-8 gap-1" onClick={quarantineAll} disabled={busy} data-testid="review-quarantine-all-btn">
                <FlaskConical className="w-3.5 h-3.5" /> Move {scan.matched} to review
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Queue list */}
      <Card className="rounded-lg">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8 h-9"
                placeholder="Search the review queue by name, email or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(search); }}
                data-testid="review-search-input"
              />
              {search && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => { setSearch(""); load(""); }}
                  data-testid="review-search-clear"
                ><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <Button size="sm" variant="outline" className="h-9" onClick={() => load(search)} disabled={loading} data-testid="review-search-btn">Search</Button>
            {canManage && selected.size > 0 && (
              <Button size="sm" className="h-9 gap-1" onClick={() => restore([...selected])} disabled={busy} data-testid="review-restore-selected-btn">
                <Undo2 className="w-3.5 h-3.5" /> Restore {selected.size} selected
              </Button>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground mb-2" data-testid="review-queue-count">
            {queue.total} profile{queue.total === 1 ? "" : "s"} in the review queue
            {search ? ` · ${queue.rows.length} matching “${search}”` : ""}
          </p>

          {queue.rows.length === 0 ? (
            <div className="p-8 text-center" data-testid="review-queue-empty">
              <ShieldCheck className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-sm font-medium">Nothing waiting for review.</p>
              <p className="text-xs text-muted-foreground mt-1">Quarantined profiles appear here until a team member restores them.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="review-queue-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={allChecked}
                        onChange={() => setSelected(allChecked ? new Set() : new Set(allOnPage))}
                        data-testid="review-select-all"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead className="text-right">Appts</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead>Flagged because</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queue.rows.map((r) => (
                    <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={selected.has(r.id)}
                          onChange={() => toggle(r.id)}
                          data-testid={`review-select-${r.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{r.full_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground break-all">{r.email || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.phone || "—"}</TableCell>
                      <TableCell className="text-right text-xs">{r.appointments_count}</TableCell>
                      <TableCell className="text-right text-xs">{r.orders_count}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.reason || "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Link
                            to={`/staff/brand/customers/${r.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5"
                            data-testid={`review-open-${r.id}`}
                          >Open <ArrowUpRight className="w-3 h-3" /></Link>
                          {canManage && (
                            <Button size="sm" variant="outline" className="h-7 px-2 gap-1" onClick={() => restore([r.id])} disabled={busy} data-testid={`review-restore-${r.id}`}>
                              <Undo2 className="w-3 h-3" /> Restore
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
